/* Copyright (c) Trimble Inc. All rights reserved. */

import React, { useEffect, useState } from 'react';
import { Logger } from '@tcp-base/toolkit';
import TokenValidationHelper from '../../helpers/TokenValidator.helper';

/**
 * Props for withAuthGuard HOC
 */
export interface WithAuthGuardOptions {
  /**
   * Optional custom loading component
   */
  LoadingComponent?: React.ComponentType;
  /**
   * Optional callback when auth validation fails
   */
  onAuthFail?: () => void;
}

/**
 * Loading component displayed while checking authentication
 */
const DefaultLoadingComponent: React.FC = () => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem',
      }}
      data-testid='auth-guard-loading'
    >
      <div
        style={{
          width: '50px',
          height: '50px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #0063a3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}
      />
      <p style={{ color: '#666', fontSize: '16px' }}>
        Authenticating... Please wait for login screen
      </p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

/**
 * Higher-Order Component that guards routes with authentication check
 *
 * Validates if a valid access token exists before rendering the wrapped component.
 * Shows a loading screen if no valid token is found.
 *
 * Uses TokenValidationHelper.validateAccessToken() which leverages:
 * - JWTParseHelper.isJWTExpired(token, JWT_TOKEN_EXPIRATION_BUFFER_SECONDS)
 * - Token.store for token retrieval
 *
 * @example
 * ```typescript
 * const ProtectedPage = withAuthGuard(MyPage);
 *
 * // With custom loading component
 * const ProtectedPage = withAuthGuard(MyPage, {
 *   LoadingComponent: CustomLoader,
 *   onAuthFail: () => // Auth failed
 * });
 * ```
 */
export function withAuthGuard<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: WithAuthGuardOptions,
): React.FC<P> {
  const LoadingComponent = options?.LoadingComponent || DefaultLoadingComponent;
  const onAuthFail = options?.onAuthFail;

  const AuthGuardedComponent: React.FC<P> = (props: P) => {
    const [isValidating, setIsValidating] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
      const validateAuth = async (): Promise<void> => {
        try {
          Logger.info('AuthGuard: Validating access token...');

          // Use TokenValidationHelper which internally uses JWTParseHelper.isJWTExpired
          const validation = await TokenValidationHelper.validateAccessToken();

          Logger.info('AuthGuard: Validation result:', {
            exists: validation.exists,
            expired: validation.expired,
            valid: validation.valid,
            hasToken: !!validation.token,
          });

          if (validation.valid) {
            Logger.info('AuthGuard: Valid token found, allowing access');
            setIsAuthenticated(true);
          } else {
            Logger.warn('AuthGuard: No valid token found', {
              exists: validation.exists,
              expired: validation.expired,
            });
            setIsAuthenticated(false);

            // Call optional callback on auth failure
            if (onAuthFail) {
              onAuthFail();
            }
          }
        } catch (error) {
          Logger.error('AuthGuard: Token validation failed:', error);
          setIsAuthenticated(false);
          if (onAuthFail) {
            onAuthFail();
          }
        } finally {
          setIsValidating(false);
        }
      };

      validateAuth();
    }, [onAuthFail]);

    // Show loading screen while validating
    if (isValidating) {
      return <LoadingComponent />;
    }

    // If not authenticated, show loading screen (waiting for login)
    if (!isAuthenticated) {
      return <LoadingComponent />;
    }

    // Render the protected component
    return <WrappedComponent {...props} />;
  };

  // Set display name for better debugging
  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name;
  AuthGuardedComponent.displayName = `withAuthGuard(${wrappedComponentName})`;

  return AuthGuardedComponent;
}

export default withAuthGuard;
